/**
 * Cola persistente de cambios (IndexedDB): sobrevive a cerrar la app y a quedarse sin conexión.
 * Se procesa en orden; cada orden es idempotente, así que reintentar es seguro.
 */
import { executeCommand, type Command, type CommandContext, type SheetsApi } from '@pf/workbook';
import { COMMANDS, tx } from '../adapters/indexeddb-cache.js';

export interface QueuedCommand { key?: number; sourceId: string; cmd: Command; label: string; attempts: number; status: 'pending' | 'conflict'; error: string; createdAt: string }

export const queue = {
  async add(sourceId: string, cmd: Command, label: string): Promise<void> {
    const q: QueuedCommand = { sourceId, cmd, label, attempts: 0, status: 'pending', error: '', createdAt: new Date().toISOString() };
    await tx(COMMANDS, 'readwrite', s => s.add(q));
  },
  async list(sourceId: string): Promise<QueuedCommand[]> {
    try { return (await tx<QueuedCommand[]>(COMMANDS, 'readonly', s => s.getAll())).filter(c => c.sourceId === sourceId); } catch { return []; }
  },
  remove: (key: number) => tx(COMMANDS, 'readwrite', s => s.delete(key)),
  put: (q: QueuedCommand) => tx(COMMANDS, 'readwrite', s => s.put(q)),
};

export interface ProcessResult { done: number; conflicts: number; stoppedBy: string }

/** Procesa los pendientes en orden. Se detiene ante un error de red (seguirá en el próximo intento). */
export async function processQueue(sourceId: string, api: SheetsApi, ctx: CommandContext = {}): Promise<ProcessResult> {
  let done = 0, conflicts = 0;
  for (const q of await queue.list(sourceId)) {
    if (q.status !== 'pending') { conflicts++; continue; }
    q.attempts++;
    try {
      const r = await executeCommand(api, q.cmd, q.attempts, ctx);
      if (r.status === 'done') { await queue.remove(q.key!); done++; }
      else { q.status = 'conflict'; q.error = r.message; await queue.put(q); conflicts++; }
    } catch (err) {
      q.error = (err as Error).message;
      await queue.put(q);
      return { done, conflicts, stoppedBy: q.error };
    }
  }
  return { done, conflicts, stoppedBy: '' };
}
